import { useCallback, useState } from "react";
import { Plus } from "lucide-react";

import { AssetsService, type AssetBaseResponse } from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/shared/page-header";
import { AssetCreateDialog } from "@/components/assets/asset-create-dialog";
import { AssetDeleteDialog } from "@/components/assets/asset-delete-dialog";
import {
  AssetsFilterBar,
  type AssetFilterState,
} from "@/components/assets/assets-filter-bar";
import { AssetsList } from "@/components/assets/assets-list";
import { AssetsQuickstart } from "@/components/assets/assets-quickstart";

const EMPTY_FILTERS: AssetFilterState = {
  asset_type: null,
  lifecycle_state: null,
  risk_classification: null,
};

export default function AssetsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AssetFilterState>(EMPTY_FILTERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AssetBaseResponse | null>(
    null
  );

  const fetcher = useCallback(
    () =>
      AssetsService.list({
        limit: 200,
        asset_type: filters.asset_type ?? undefined,
        lifecycle_state: filters.lifecycle_state ?? undefined,
        risk_classification: filters.risk_classification ?? undefined,
      }),
    [filters]
  );

  const { data, error, isLoading, refetch } = useApi(fetcher, {
    deps: [filters],
  });

  const items = data?.items ?? [];
  const hasActiveFilters =
    filters.asset_type !== null ||
    filters.lifecycle_state !== null ||
    filters.risk_classification !== null;
  const hasNoAssetsAtAll =
    !isLoading && !hasActiveFilters && items.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("assets.page.title")}
        description={t("assets.page.description")}
        action={
          <Button onClick={() => setCreateOpen(true)} size="sm">
            <Plus size={16} />
            {t("assets.actions.create")}
          </Button>
        }
      />

      {error && <Alert variant="danger">{parseApiError(error)}</Alert>}

      {hasNoAssetsAtAll ? (
        <AssetsQuickstart onCreateClick={() => setCreateOpen(true)} />
      ) : (
        <>
          <AssetsFilterBar value={filters} onChange={setFilters} />
          <AssetsList
            items={items}
            isLoading={isLoading}
            onDelete={setDeleteTarget}
          />
        </>
      )}

      <AssetCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          void refetch();
        }}
      />

      <AssetDeleteDialog
        asset={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={() => {
          setDeleteTarget(null);
          void refetch();
        }}
      />
    </div>
  );
}
